<script setup>
import {Gradient} from "@/assets/js/Gradient";
import {useMainStore} from "@/store/MainStore";
import {ref, computed, onBeforeMount, onBeforeUnmount, onMounted} from "vue";
import logo from "@/assets/img/calid_logo_small.png";
import "@/assets/css/auth.css";

// Initialise
const gradient = new Gradient();
const store = useMainStore();
const body = document.getElementsByTagName("body")[0];

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const password = ref('');
const confirmPassword = ref('');
const errorMessage = ref('');

// Check if fields have vals
const hasFirstName = computed(() => firstName.value.trim() !== "");
const hasLastName = computed(() => lastName.value.trim() !== "");
const hasEmail = computed(() => email.value.trim() !== "");
const hasPassword = computed(() => password.value.trim() !== "");
const hasConfirmPassword = computed(() => confirmPassword.value.trim() !== "");

// Add validation
const error = computed(() => {
  if (errorMessage.value) {
    return true;
  }
  return false;
});

const validateSignUp = () => {
  if (!hasFirstName.value) {
    errorMessage.value = "Please enter your first name";
    return false;
  }
  if (!hasLastName.value) {
    errorMessage.value = "Please enter your last name";
    return false;
  }
  if (!hasEmail.value || email.value.indexOf('@') === -1) {
    errorMessage.value = "Please enter a valid email";
    return false;
  }
  if (!hasPassword.value) {
    errorMessage.value = "Please enter your password";
    return false;
  }
  if (password.value !== confirmPassword.value) {
    errorMessage.value = "Passwords do not match";
    return false;
  }
  return true;
}

const handleSignUp = async () => {
  try {
    if (!validateSignUp()) {
      return;
    }
    const API_URL = import.meta.env.VITE_API_URL || '/api';
    const response = await fetch(API_URL + '/v1/signup', {
      method: 'POST',
      body: JSON.stringify({
        first_name: firstName.value,
        last_name: lastName.value,
        email: email.value,
        password: password.value,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();
    if (data.status === "error") {
      errorMessage.value = data.message;
      return;
    }

    if (response.status === 200 || response.status === 201) {
      console.log('Registration successful');
      // Redirect to sign in page
      window.location.href = '/signin?registered=true';
    } else {
      errorMessage.value = 'Registration failed: ' + (data.errors ? data.errors.join(', ') : data.message);
    }
  } catch (error) {
    console.error('Registration failed: ', error);
  }
}

// Lc hooks
onBeforeMount(() => {
  store.layout = "auth";
  store.hideConfigButton = true;
  store.showNavbar = false;
  store.showSidenav = false;
  store.showFooter = false;
  body.classList.remove("bg-gray-100");
});
onBeforeUnmount(() => {
  store.hideConfigButton = false;
  store.showNavbar = true;
  store.showSidenav = true;
  store.showFooter = true;
  body.classList.add("bg-gray-100");
});
onMounted(() => {
  gradient.initGradient('#gradient-canvas');
});
</script>

<template>
  <div class="container-fluid" id="header-gradient">
    <canvas class="rounded-2" id="gradient-canvas"></canvas>
  </div>
  <div id="auth-container">
    <form class="login-form validate-form" method="POST" @submit.prevent="handleSignUp">
      <span class="title pb-2">
        <img class="auth-logo" :src="logo" alt="Calid Logo"/>
      </span>
      <span class="title mb-3">Calid</span>
      <span class="subtitle mb-5">Create Your Account</span>
      <p class="error-message" v-if="errorMessage">{{ errorMessage }}</p>

      <div class="row">
        <div class="col-md-6">
          <div class="wrapper-input validate-input" data-validate="Enter first name">
            <input
                class="input pass-font"
                type="text"
                name="first-name"
                id="first-name"
                v-model="firstName"
                required
                :class="{ 'has-val': hasFirstName }"
            />
            <span
                class="focus-input"
                :class="{ 'input-error': error }"
                data-placeholder="First Name"
            ></span>
          </div>
        </div>
        <div class="col-md-6">
          <div class="wrapper-input validate-input" data-validate="Enter last name">
            <input
                class="input pass-font"
                type="text"
                name="last-name"
                id="last-name"
                v-model="lastName"
                required
                :class="{ 'has-val': hasLastName }"
            />
            <span
                class="focus-input"
                :class="{ 'input-error': error }"
                data-placeholder="Last Name"
            ></span>
          </div>
        </div>
      </div>

      <div class="wrapper-input validate-input" data-validate="Example: john@doe.com">
        <input
            class="input pass-font"
            type="email"
            name="email"
            id="email"
            v-model="email"
            required
            :class="{ 'has-val': hasEmail }"
        />
        <span
            class="focus-input"
            :class="{ 'input-error': error }"
            data-placeholder="Email"
        ></span>
      </div>

      <div class="wrapper-input validate-input" data-validate="Enter password">
        <input
            class="input pass-font"
            type="password"
            name="password"
            id="password"
            v-model="password"
            required
            :class="{ 'has-val': hasPassword }"
        />
        <span
            class="focus-input"
            :class="{ 'input-error': error }"
            data-placeholder="Password"
        ></span>
      </div>

      <div class="wrapper-input validate-input" data-validate="Confirm password">
        <input
            class="input pass-font"
            type="password"
            name="confirm-password"
            id="confirm-password"
            v-model="confirmPassword"
            required
            :class="{ 'has-val': hasConfirmPassword }"
        />
        <span
            class="focus-input"
            :class="{ 'input-error': error }"
            data-placeholder="Confirm Password"
        ></span>
      </div>

      <div class="container-btn mt-3">
        <div class="wrap-btn">
          <div class="bg-btn"></div>
          <button class="form-btn">
            Sign Up
          </button>
        </div>
      </div>
      <hr/>

      <div class="text-center mt-3">
        <span class="text-1">Already have an account?</span>
        <a class="text-2 px-1" href="/signin">Sign In</a>
      </div>
      <footer class="text-center mt-2">
        <small class="text-small text-muted">&copy; 2024 Zach Matcham</small>
      </footer>
    </form>
  </div>
</template>

<style scoped>

</style>